export type DirectKgQuantityMode = "legacy_decimal" | "whole";

export type DirectKgQuantityItem = {
  unit_type?: string | null;
  direct_kg_quantity_mode?: DirectKgQuantityMode | null;
};

export type WholeDirectKgQuantityInputReason =
  | "blank"
  | "invalid"
  | "fraction"
  | "zero"
  | "negative";

export type WholeDirectKgQuantityInputValidation =
  | { quantity: number; reason: null }
  | { quantity: null; reason: WholeDirectKgQuantityInputReason };

function roundToGram(value: number) {
  return Math.round(value * 1000) / 1000;
}

export function isDirectKgUnit(unitType?: string | null) {
  return unitType === "kg";
}

export function isLegacyDirectKgItem<Item extends DirectKgQuantityItem>(
  item: Item,
) {
  return (
    isDirectKgUnit(item.unit_type) &&
    item.direct_kg_quantity_mode === "legacy_decimal"
  );
}

export function getWholeDirectKgQuantityInputAttributes() {
  return {
    min: 1,
    step: 1,
    inputMode: "numeric" as const,
  };
}

export function getWholeDirectKgNonNegativeQuantityInputAttributes() {
  return {
    min: 0,
    step: 1,
    inputMode: "numeric" as const,
  };
}

export function validateWholeDirectKgQuantityInput(
  rawValue: string,
): WholeDirectKgQuantityInputValidation {
  const normalized = rawValue.trim().replace(",", ".");
  if (!normalized) return { quantity: null, reason: "blank" };

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return { quantity: null, reason: "invalid" };
  if (parsed < 0) return { quantity: null, reason: "negative" };
  if (parsed === 0) return { quantity: null, reason: "zero" };
  if (!Number.isInteger(parsed)) return { quantity: null, reason: "fraction" };
  return { quantity: parsed, reason: null };
}

export function validateWholeDirectKgNonNegativeQuantityInput(
  rawValue: string,
): WholeDirectKgQuantityInputValidation {
  const normalized = rawValue.trim().replace(",", ".");
  if (!normalized) return { quantity: null, reason: "blank" };

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) return { quantity: null, reason: "invalid" };
  if (parsed < 0) return { quantity: null, reason: "negative" };
  if (!Number.isInteger(parsed)) return { quantity: null, reason: "fraction" };
  return { quantity: parsed, reason: null };
}

export function getQuantityUnitLabel(unitType?: string | null) {
  switch (unitType) {
    case "kg":
      return "kg";
    case "un":
      return "un";
    case "bdj":
      return "bdj";
    case "sc_kg":
      return "sc";
    case "cx_kg":
    case "cx_un":
      return "cx";
    default:
      return unitType ?? "";
  }
}

export function getQuantityInputStep(unitType?: string | null) {
  return isDirectKgUnit(unitType) ? "0.001" : "1";
}

export function getQuantityInputMode(unitType?: string | null) {
  return isDirectKgUnit(unitType) ? "decimal" : "numeric";
}

export function getDefaultQuantity(unitType?: string | null) {
  return isDirectKgUnit(unitType) ? 0.001 : 1;
}

export function formatQuantityDisplay(
  value: number | string | null | undefined,
  unitType?: string | null,
) {
  if (value == null || value === "") return "";
  const numeric =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));
  if (!Number.isFinite(numeric)) return String(value);
  if (!isDirectKgUnit(unitType)) return String(Math.trunc(numeric));
  return roundToGram(numeric)
    .toFixed(3)
    .replace(/\.?0+$/, "");
}

export function parsePositiveQuantityInput(
  rawValue: string,
  unitType?: string | null,
) {
  const normalized = rawValue.trim().replace(",", ".");
  if (!normalized) return null;
  if (isDirectKgUnit(unitType)) {
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return roundToGram(parsed);
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
}

export function parseNonNegativeQuantityInput(
  rawValue: string,
  unitType?: string | null,
) {
  const normalized = rawValue.trim().replace(",", ".");
  if (!normalized) return null;
  if (isDirectKgUnit(unitType)) {
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed < 0) return null;
    return roundToGram(parsed);
  }
  const parsed = Number(normalized);
  if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
}
